import React, { forwardRef, useId, useRef, useState } from "react";
import clsx from "clsx";

import { trackEvent } from "utility/mixpanel";

import styles from "./text-field.module.scss";

type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "id" | "type"
>;

export interface TextFieldProps extends InputProps {
  label: string;
  /** Visible help text below the field (success/info). */
  helper?: React.ReactNode;
  /** Error message — replaces helper and applies error styling. */
  error?: React.ReactNode;
  /**
   * Force the red error border without rendering an error message. Useful
   * when several fields share a single error message rendered elsewhere
   * (e.g. login error shown beneath "Forgot password?", but both email
   * and password fields outlined in red).
   */
  hasError?: boolean;
  /** Type of input. Password renders the Show/Hide toggle. */
  type?: "email" | "password" | "text";
  /** Wrap a custom render of the input area (rarely needed). */
  className?: string;
  /** Class for the wrapping field group. */
  groupClassName?: string;
  /**
   * WONE-713: when both ``trackingScreen`` and ``trackingField`` are set,
   * the field emits ``Auth Field Focused`` / ``Auth Field Blurred``
   * Mixpanel events. Pass these from the parent screen so PMs can see
   * which step in the funnel the interaction belongs to. Never sends the
   * raw value — only ``Had Value`` (bool) on blur.
   */
  trackingScreen?: string;
  trackingField?: string;
}

/**
 * Border state resolves in priority order (mirrors mobile TextField):
 *   error    → red
 *   focused  → charcoal
 *   filled   → transparent (no visible border once user has typed)
 *   default  → soft grey
 *
 * Filled/focus is tracked locally rather than via :placeholder-shown so we
 * can support controlled inputs (`value="..."`) the same way mobile does.
 */
export const TextField = forwardRef<
  HTMLInputElement,
  TextFieldProps
>(
  (
    {
      label,
      helper,
      error,
      hasError: hasErrorProp,
      type = "text",
      className,
      groupClassName,
      onFocus,
      onBlur,
      onChange,
      defaultValue,
      value,
      trackingScreen,
      trackingField,
      ...rest
    },
    ref
  ): JSX.Element => {
    const reactId = useId();
    const inputId = rest.name
      ? `auth-field-${rest.name}-${reactId}`
      : `auth-field-${reactId}`;
    const [reveal, setReveal] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    // Track whether the field currently has any content. For controlled
    // inputs we read `value` directly; for uncontrolled we sample the DOM
    // via onChange.
    const initiallyFilled =
      (typeof value === "string" && value.length > 0) ||
      (typeof defaultValue === "string" && defaultValue.length > 0);
    const [hasValue, setHasValue] = useState(initiallyFilled);
    // Snapshot the actual value at focus so blur fires whenever the
    // value changed during the focus session (e.g. editing an already-
    // filled email), not just on empty↔filled flips — keeps Mixpanel
    // volume sane while not under-reporting edits. The raw string is
    // only ever compared, never sent (``Had Value`` is a bool).
    const valueAtFocusRef = useRef<string>("");
    const trackingEnabled = Boolean(trackingScreen && trackingField);

    const isPassword = type === "password";
    const resolvedType = isPassword && reveal ? "text" : type;
    const hasError = Boolean(error) || Boolean(hasErrorProp);

    // Resolve the current visual state (highest-priority wins).
    let visualState: "error" | "focused" | "filled" | "default";
    if (hasError) visualState = "error";
    else if (isFocused) visualState = "focused";
    else if (hasValue) visualState = "filled";
    else visualState = "default";

    return (
      <div className={clsx(styles["field-group"], groupClassName)}>
        <label htmlFor={inputId} className={styles["field-label"]}>
          {label}
        </label>
        <div
          className={clsx(
            styles["field-control"],
            styles[`field-control-${visualState}`]
          )}
        >
          <input
            ref={ref}
            id={inputId}
            type={resolvedType}
            className={clsx(styles["field-input"], className)}
            aria-invalid={hasError || undefined}
            aria-describedby={
              [hasError ? `${inputId}-error` : null, helper ? `${inputId}-helper` : null]
                .filter(Boolean)
                .join(" ") || undefined
            }
            value={value}
            defaultValue={defaultValue}
            onFocus={(event) => {
              setIsFocused(true);
              if (trackingEnabled) {
                valueAtFocusRef.current = event.target.value;
                trackEvent("Auth Field Focused", {
                  Field: trackingField,
                  Screen: trackingScreen,
                });
              }
              onFocus?.(event);
            }}
            onBlur={(event) => {
              setIsFocused(false);
              if (
                trackingEnabled &&
                valueAtFocusRef.current !== event.target.value
              ) {
                trackEvent("Auth Field Blurred", {
                  Field: trackingField,
                  Screen: trackingScreen,
                  "Had Value": event.target.value.length > 0,
                });
              }
              onBlur?.(event);
            }}
            onChange={(event) => {
              setHasValue(event.target.value.length > 0);
              onChange?.(event);
            }}
            {...rest}
          />
          {isPassword && (
            <button
              type="button"
              className={styles["field-reveal"]}
              onClick={() => setReveal((current) => !current)}
              tabIndex={-1}
              aria-label={reveal ? "Hide password" : "Show password"}
            >
              {reveal ? "Hide" : "Show"}
            </button>
          )}
        </div>
        {/* Error and helper are rendered as siblings so the rule ("Your
            password must have…") stays visible even while an error is
            showing — mirrors mobile AuthLayout. */}
        {hasError && (
          <p
            id={`${inputId}-error`}
            className={clsx(styles["field-helper"], styles["field-helper-error"])}
          >
            {error}
          </p>
        )}
        {helper && (
          <p
            id={`${inputId}-helper`}
            className={styles["field-helper"]}
          >
            {helper}
          </p>
        )}
      </div>
    );
  }
);

TextField.displayName = "TextField";

export default TextField;
